import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,
  CircularProgress,
  Divider,
  IconButton
} from "@mui/material";
import useLogout from "../../context/Logout";
import { getUserProfile } from "../../Services/ClintsApi";
import DashboardIcon from '@mui/icons-material/Dashboard';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import DomainIcon from '@mui/icons-material/Domain';
import FeaturesIcon from '@mui/icons-material/Stars';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LogoutIcon from '@mui/icons-material/Logout';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const UserDashboard = () => {
  const logout = useLogout();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getUserProfile()
      .then((res) => {
        setUserData(res.data);
      })
      .catch(() => {
        setUserData(null);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  if (!userData) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography variant="h6" color="error">
          حدث خطأ أثناء تحميل البيانات
        </Typography>
        <Button variant="contained" onClick={logout} sx={{ mt: 2 }}>
          تسجيل الخروج
        </Button>
      </Box>
    );
  }

  const { user, tenant } = userData;

  return (
    <Box sx={{
      p: 3,
      background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
      minHeight: "100vh",
      direction: "rtl"
    }}>
      {/* Welcome Header */}
      <Box sx={{
        textAlign: "center",
        mb: 4,
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        borderRadius: "20px",
        p: 4,
        color: "white",
        boxShadow: "0 10px 30px rgba(102, 126, 234, 0.3)"
      }}>
        <Avatar sx={{
          width: 80,
          height: 80,
          mx: "auto",
          mb: 2,
          bgcolor: "rgba(255,255,255,0.2)"
        }}>
          <DashboardIcon sx={{ fontSize: 40 }} />
        </Avatar>
        <Typography variant="h3" sx={{ fontWeight: "bold", mb: 2 }}>
          لوحة التحكم الرئيسية
        </Typography>
        <Typography variant="h5" sx={{ opacity: 0.9, mb: 1 }}>
          مرحباً بك، {user.name}!
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.8 }}>
          تابع إحصائياتك واستمتع بخدماتنا المتميزة
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* User Info Card */}
        <Grid item xs={12} md={6} lg={4}>
          <Card sx={{
            height: "100%",
            borderRadius: "20px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            boxShadow: "0 8px 32px rgba(102, 126, 234, 0.3)"
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", mr: 2 }}>
                  <PersonIcon />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  المعلومات الشخصية
                </Typography>
              </Box>

              <Typography variant="body1" sx={{ mb: 1, opacity: 0.9 }}>
                📧 {user.email}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1, opacity: 0.9 }}>
                📱 {user.phone}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, opacity: 0.9 }}>
                🔢 {user.code}
              </Typography>
              <Chip
                label={user.active ? "نشط" : "غير نشط"}
                color={user.active ? "success" : "error"}
                sx={{ fontWeight: "bold" }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Tenant Info Card */}
        <Grid item xs={12} md={6} lg={4}>
          <Card sx={{
            height: "100%",
            borderRadius: "20px",
            background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
            color: "white",
            boxShadow: "0 8px 32px rgba(240, 147, 251, 0.3)"
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", mr: 2 }}>
                  <BusinessIcon />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  بيانات النظام
                </Typography>
              </Box>

              <Typography variant="body1" sx={{ mb: 1, opacity: 0.9 }}>
                🏢 {tenant.id}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1, opacity: 0.9 }}>
                🗄️ {tenant.tenancy_db_name}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, opacity: 0.9 }}>
                💾 {tenant.database_backup}
              </Typography>
              <Chip
                label={tenant.is_demo ? "نسخة تجريبية" : "نسخة مدفوعة"}
                color={tenant.is_demo ? "warning" : "success"}
                sx={{ fontWeight: "bold" }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Stats Card */}
        <Grid item xs={12} lg={4}>
          <Card sx={{
            height: "100%",
            borderRadius: "20px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.1)"
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 3, color: "#1976d2" }}>
                إحصائيات سريعة
              </Typography>

              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                <Box sx={{ textAlign: "center" }}>
                  <Avatar sx={{ bgcolor: "#2196f3", mx: "auto", mb: 1 }}>
                    <DomainIcon />
                  </Avatar>
                  <Typography variant="h4" sx={{ fontWeight: "bold", color: "#1976d2" }}>
                    {tenant.domains.length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    النطاقات
                  </Typography>
                </Box>

                <Box sx={{ textAlign: "center" }}>
                  <Avatar sx={{ bgcolor: "#ff9800", mx: "auto", mb: 1 }}>
                    <FeaturesIcon />
                  </Avatar>
                  <Typography variant="h4" sx={{ fontWeight: "bold", color: "#f57c00" }}>
                    {tenant.features.length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    المميزات
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Domains Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{
            height: "100%",
            borderRadius: "20px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.1)"
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Avatar sx={{ bgcolor: "#2196f3", mr: 2 }}>
                  <DomainIcon />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: "bold", color: "#1976d2" }}>
                  الذهاب الي نسختي
                </Typography>
              </Box>

              {tenant.domains.map((domain, index) => (
                <Box key={index} sx={{
                  p: 2,
                  mb: 1,
                  backgroundColor: "#f8f9fa",
                  borderRadius: "8px",
                  border: "1px solid #e9ecef"
                }}>
                  <Typography variant="body2" sx={{ wordBreak: "break-all", display: "inline-block", mr: 1 }}>
                    🌐 {domain.domain}
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label={`فتح ${domain.domain} في تاب جديد`}
                    onClick={() => {
                      try {
                        let url = String(domain.domain).trim();
                        if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
                        window.open(url, "_blank", "noopener,noreferrer");
                      } catch (e) {
                        console.error("open domain error:", e);
                      }
                    }}
                    sx={{ verticalAlign: "middle" }}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                  <Typography variant="caption" color="textSecondary">
                    تم الإنشاء: {new Date(domain.created_at).toLocaleDateString('ar-EG')}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Features Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{
            height: "100%",
            borderRadius: "20px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.1)"
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Avatar sx={{ bgcolor: "#ff9800", mr: 2 }}>
                  <FeaturesIcon />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: "bold", color: "#ff9800" }}>
                  المميزات النشطة
                </Typography>
              </Box>

              {tenant.features.map((feature) => (
                <Box key={feature.id} sx={{
                  p: 2,
                  mb: 2,
                  backgroundColor: "#fff3e0",
                  borderRadius: "8px",
                  border: "1px solid #ffcc02"
                }}>
                  <Typography variant="body1" sx={{ fontWeight: "bold", mb: 1 }}>
                    {feature.name}
                  </Typography>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="caption">شهري: {feature.pivot.monthly_cost} جنيه</Typography>
                    <Typography variant="caption">سنوي: {feature.pivot.yearly_cost} جنيه</Typography>
                  </Box>
                  <Typography variant="caption" color="textSecondary">
                    {feature.pivot.start_date} - {feature.pivot.end_date}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Account Timeline */}
        <Grid item xs={12}>
          <Card sx={{
            borderRadius: "20px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.1)"
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Avatar sx={{ bgcolor: "#4caf50", mr: 2 }}>
                  <CalendarTodayIcon />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: "bold", color: "#4caf50" }}>
                  الجدول الزمني للحساب
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, backgroundColor: "#e8f5e8", borderRadius: "8px" }}>
                    <Typography variant="body1" sx={{ fontWeight: "bold", mb: 1 }}>
                      تاريخ إنشاء المستخدم
                    </Typography>
                    <Typography variant="body2">
                      {new Date(user.created_at).toLocaleDateString('ar-EG')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, backgroundColor: "#e3f2fd", borderRadius: "8px" }}>
                    <Typography variant="body1" sx={{ fontWeight: "bold", mb: 1 }}>
                      تاريخ إنشاء النظام
                    </Typography>
                    <Typography variant="body2">
                      {new Date(tenant.created_at).toLocaleDateString('ar-EG')}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Logout Button */}
      <Box sx={{ textAlign: "center", mt: 4 }}>
        <Button
          variant="contained"
          color="error"
          size="large"
          onClick={logout}
          startIcon={<LogoutIcon />}
          sx={{
            borderRadius: "25px",
            px: 4,
            py: 1.5,
            fontSize: "1.1rem",
            fontWeight: "bold"
          }}
        >
          تسجيل الخروج
        </Button>
      </Box>
    </Box>
  );
};

export default UserDashboard;